import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { FinanceTrendDto } from '../../../common/interfaces/invoicing.interface';
import { formatMoney } from '../../../utilities/helper/money.helper';
import { formatMonthKeyShort } from '../../../utilities/helper/date.helper';
import { STAT_TONES } from '../../../components/StatTile';
import { useTranslation } from 'react-i18next';

import { palette } from '../../../theme';
/**
 * Charged against received, month by month, for whatever scope the page is showing.
 *
 * This is the chart the library was added for: the four tiles say what this month looks
 * like, and only this says whether that is good or bad.
 *
 * It carries no outstanding line on purpose. That figure is all-time, and a real trend of
 * it needs the balance as it stood at each month end - which the database does not keep.
 * A debt trend drawn from today's paid amounts would look convincing and be wrong.
 *
 * Clicking a bar selects that month, so the chart and the stepper are never out of step.
 */
const FinanceTrendChart: React.FC<{
  trend: FinanceTrendDto;
  onSelectMonth: (month: string) => void;
  /** The month the page shows; its label is marked. MUI's bar chart has no per-column background. */
  selectedMonth?: string;
}> = ({ trend, onSelectMonth, selectedMonth }) => {
  const { t } = useTranslation();
  if (trend.points.length === 0) return null;

  const keys = trend.points.map(point => `${point.year}-${String(point.month).padStart(2, '0')}`);
  const legend = [
    { label: t('finance.charts.issued'), color: palette.chart.charged },
    { label: t('finance.charts.received'), color: STAT_TONES.credit.accent }
  ];

  return (
    <Box sx={{ border: `1px solid ${palette.line}`, borderRadius: 4, p: 3, flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1 }}>
        <Typography component="h2" sx={{ fontSize: 16, fontWeight: 600 }}>
          {t('finance.charts.chargedVsReceived', { count: trend.points.length })}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {legend.map(entry => (
            <Box key={entry.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 13, color: 'text.secondary' }}>
              <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: entry.color }} />
              {entry.label}
            </Box>
          ))}
        </Box>
      </Box>
      <BarChart
        height={260}
        slotProps={{ legend: { hidden: true } }}
        xAxis={[
          {
            scaleType: 'band',
            data: keys,
            // the chosen month's label is marked; there is no per-column background to give it
            valueFormatter: (key: string) => (key === selectedMonth ? `▸ ${formatMonthKeyShort(key)}` : formatMonthKeyShort(key))
          }
        ]}
        series={[
          {
            data: trend.points.map(point => point.chargedInMonth),
            label: t('finance.charts.issued'),
            color: palette.chart.charged,
            valueFormatter: value => formatMoney(value ?? 0)
          },
          {
            data: trend.points.map(point => point.paidInMonth),
            label: t('finance.charts.received'),
            color: STAT_TONES.credit.accent,
            valueFormatter: value => formatMoney(value ?? 0)
          }
        ]}
        onItemClick={(_event, item) => {
          const key = item?.dataIndex !== undefined ? keys[item.dataIndex] : undefined;
          if (key) onSelectMonth(key);
        }}
        margin={{ left: 70 }}
      />
      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t('finance.charts.clickHint')}</Typography>
    </Box>
  );
};

export default FinanceTrendChart;
